import {
	type BenchmarkSummary,
	type InvestmentStatus,
	BenchmarksControllerApiFactory,
	IntegrationReferenceControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { useFunctionalAreas } from "$root/App/context";
import { useApiGen } from "$root/api/hooks";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LabelRounded } from "$root/components/LabelRounded/Index";
import { PageDownloadAndMoreActionsMenu } from "$root/components/PageDownloadAndMoreActionsMenu";
import { type PageHeaderProps, PageHeader } from "$root/components/PageHeader";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import ReactQueryWrapper, { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import ReviewCompositionErrors from "$root/components/ReviewCompositionErrors";
import { useEventBus } from "$root/event-bus";
import { spawnAccessDialog } from "$root/functional-areas/acl/AccessDialog";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import EntityStatus from "$root/functional-areas/acl/EntityStatus";
import { useBenchmarkEntityManagementActions } from "$root/functional-areas/benchmark/entity-management";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import {
	type CompositionInRetrievingDataResponse,
	getCompositionDataInRetrievingData,
} from "$root/functional-areas/instruments/repository";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { useUserValue } from "$root/functional-areas/user";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import type { CustomAxiosError } from "$root/third-party-integrations/axios";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { downloadContentDisposition, objMatchFn } from "$root/utils";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { BenchmarkContext } from "$root/widgets-architecture/contexts/benchmark";
import { Card } from "$root/widgets-architecture/layout/Card";
import WidgetsMapper from "$root/widgets-architecture/layout/WidgetsMapper";
import { Banner, Text } from "@mdotm/shadcn/legacy/components";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { PortfolioStudioTab } from "../PortfoliosStudio/portfolio-studio-tabs";
import { navigateToReviewIfAnythingToReviewSync } from "$root/functional-areas/instruments/review";
import { ReviewEntity } from "../ReviewInstruments.const";

const severityByStatus: Record<InvestmentStatus, PageHeaderProps["severity"]> = {
	CALCULATING: "calculating",
	ERROR: "calculating", // Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
	READY: "success",
	ACCEPTED: "success",
	PROPOSAL_READY: "success",
	RETRIEVING_DATA: "calculating",
	REVIEW: undefined,
	DRAFT: "info",
	EXECUTING: "calculating",
};

type PageHeaderSubtitleSummary = {
	summary?: BenchmarkSummary;
	entityManagementActions: ReturnType<typeof useBenchmarkEntityManagementActions>;
};

const PageHeaderSubtitle: FC<PageHeaderSubtitleSummary> = (props) => {
	const { summary } = props;
	const { richAcl } = summary ?? {};
	const { t } = useTranslation();

	const { formatDate } = useLocaleFormatters();
	const benchmarkV4Api = useApiGen(BenchmarksControllerApiFactory);
	const integrationsApi = useApiGen(IntegrationReferenceControllerApiFactory);
	const user = useUserValue();

	return (
		<div className="flex justify-between items-center min-h-[54px]">
			<div className="flex justify-between items-center space-x-4 text-[#585D68]">
				<div
					className="flex items-center"
					data-qualifier="BenchmarkDetails/PageHeader/Status"
					data-status={summary?.status}
				>
					<span className="mr-2 uppercase">{t("STATUS")}</span>
					<LabelRounded content={{ status: summary?.status, component: "" }} />
				</div>
				<div className="" data-qualifier="BenchmarkDetails/PageHeader/Action">
					<span className="uppercase">Last action request:</span>&nbsp;
					<span className="font-bold uppercase">
						{summary?.action} ({formatDate(summary?.modificationTime ?? "")})
					</span>
				</div>
				<EntityStatus
					accessControl={richAcl}
					entity="BENCHMARK"
					entityId={summary?.benchmarkIdentifier}
					entityName={summary?.name}
				/>
			</div>
			<div className="flex items-center gap-2">
				<PageDownloadAndMoreActionsMenu
					area="benchmark"
					downloadActions={[
						{
							icon: "xls",
							disabled: summary?.status === "CALCULATING" || !summary,
							onClickAsync: async () => {
								if (!summary?.benchmarkIdentifier) {
									throw new Error("unable to find benchmarkId of undefined");
								}
								const response = await benchmarkV4Api.exportComposition1(summary?.benchmarkIdentifier, {
									responseType: "blob",
								});
								downloadContentDisposition(response);
							},
							label: "Benchmark composition",
							"data-qualifier": "BenchmarkDetails/DropdownMenu/DropdownItem(XLSReport)",
						},
						{
							icon: "xls",
							onClickAsync: async () => {
								if (!summary?.benchmarkIdentifier) {
									throw new Error("unable to find benchmarkId of undefined");
								}
								const response = await integrationsApi.exportReference(summary?.benchmarkIdentifier, {
									responseType: "blob",
								});
								downloadContentDisposition(response);
							},
							label: "Reference template",
							"data-qualifier": "BenchmarkDetails/DropdownMenu/DropdownItem(ReferenceTemplate)",
						},
					]}
					moreActions={
						aclByArea.benchmark.canDelete(user.id, richAcl?.acl ?? [])
							? [
									{
										icon: "Delete",
										onClick: unpromisify(props.entityManagementActions.deleteAsync ?? noop),
										"data-qualifier": "BenchmarkDetails/DropdownMenu/DropdownItem(Delete)",
										disabled: !props.entityManagementActions.deleteAsync,
										label: t("BUTTON.DELETE"),
									},
								]
							: undefined
					}
				/>
			</div>
		</div>
	);
};

const LegacyBenchmark = (): React.JSX.Element => {
	const { t } = useTranslation();
	const { uuid } = useParams<{ uuid: string }>();
	const { push } = useTypedNavigation();

	useFunctionalAreas(
		() => ({
			areas: "benchmark",
			data: { uuid },
		}),
		[uuid],
	);

	const queryBenchmarkSummary = useQuery({
		...referenceRepository.benchmarkSummary.getQueryConfig(uuid ?? ""),
		enabled: Boolean(uuid),
	});

	useFunctionalAreas(
		() => ({
			areas: "benchmark",
			data: { uuid, name: queryBenchmarkSummary.data?.name },
		}),
		[uuid, queryBenchmarkSummary.data?.name],
	);
	useQueryReactor(queryBenchmarkSummary, {
		onSuccess({ status }) {
			if (status === "REVIEW") {
				navigateToReviewIfAnythingToReviewSync(uuid, ReviewEntity.BENCHMARK, () =>
					push("ReviewInstruments", { entity: ReviewEntity.BENCHMARK, uuid: uuid }),
				);
			}
		},
		onError: (error: AxiosError<CustomAxiosError>) => {
			if (error.response?.data.code === 404 || error.response?.data.message === "Accesso negato") {
				spawnAccessDialog({
					onClick: (onClose) => {
						push("PortfoliosStudio", { tab: PortfolioStudioTab.References });
						onClose();
					},
				});
				return;
			}

			platformToast({
				children: t("SOMETHING_WENT_WRONG"),
				severity: "error",
				icon: "Portfolio",
			});
			push("PortfoliosStudio", { status: "notFound", tab: PortfolioStudioTab.References });
		},
	});

	const { data: summary, refetch: refetchSummary } = queryBenchmarkSummary;

	useEventBus("benchmark-update", {
		filter: objMatchFn({ uuid }),
		listener: () => {
			refetchSummary().catch(noop);
		},
	});

	useEventBus("shared-entity", {
		filter: objMatchFn({ sharedEntityUuid: uuid }),
		listener: () => {
			refetchSummary().catch(noop);
		},
	});

	const entityManagementActions = useBenchmarkEntityManagementActions(
		{ ...summary, uuid: summary?.benchmarkIdentifier }, // why can't we have consistent DTOs? :(
		{
			onDelete: () => push("PortfoliosStudio", { tab: PortfolioStudioTab.References }),
		},
	);

	const crumbs = useCrumbs();

	return (
		<ReactQueryWrapperBase query={queryBenchmarkSummary}>
			{() => (
				<>
					<PageHeader
						severity={summary?.status ? severityByStatus[summary?.status] : undefined}
						crumbs={[
							...crumbs.portfolioStudio("References", summary?.benchmarkIdentifier),
							...crumbs.benchmarkName({
								uuid: summary?.benchmarkIdentifier,
								name: summary?.name,
							}),
						]}
						subTitle={
							summary?.status !== "REVIEW" && (
								<PageHeaderSubtitle summary={summary} entityManagementActions={entityManagementActions} />
							)
						}
					/>

					{summary?.status === "RETRIEVING_DATA" ? (
						<ReactQueryWrapper
							queryFn={() => getCompositionDataInRetrievingData(uuid, "BENCHMARK")}
							queryKey={["benchmarkCompositionInReview", summary?.status, uuid]}
						>
							{(compositionData) => <BenchmarkReviewBlock summary={summary} {...compositionData} />}
						</ReactQueryWrapper>
					) : (
						<BenchmarkContext.Provider value={{ benchmarkId: uuid }}>
							<WidgetsMapper widgetName="BenchmarkComposition" span={3} />
						</BenchmarkContext.Provider>
					)}
				</>
			)}
		</ReactQueryWrapperBase>
	);
};

function BenchmarkReviewBlock({
	summary,
	compositionWithCash,
	instruments,
}: CompositionInRetrievingDataResponse & {
	summary: BenchmarkSummary;
}) {
	const { t } = useTranslation();
	const columns = columnsDefinition.useInstrumentComposition("read");

	return (
		<Card>
			<Text type="Body/XL/Bold" className="mb-4">
				Composition
			</Text>
			<ReviewCompositionErrors errors={compositionWithCash?.uploadErrors} />
			<div className="mb-4" hidden={summary?.status === "RETRIEVING_DATA"}>
				<Banner severity="warning" title="Review Before Saving">
					Some instruments require your attention. This may be due to missing information or because they have been
					delisted. Please review the list below and make any necessary updates before saving.
				</Banner>
			</div>
			<div className="mb-4">
				<Banner severity="info">{t("PORTFOLIO_UPLOAD_ALERTS.CALCULATING_UPLOAD")}</Banner>
			</div>

			<AgGridWithModules<Instrument>
				{...agExtra.aggFunctions()}
				{...agExtra.usePersistColState(persistedColStateKeys["tableV3-reference-composition-read"])}
				columnDefs={columns}
				rowData={instruments}
				getRowId={(x) => x.data.tickerName!}
			/>
		</Card>
	);
}

export default LegacyBenchmark;
